import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Storefront, ArrowRight } from "@phosphor-icons/react";

const ORIGINS = ["Nigerian", "Ghanaian", "Kenyan", "Zimbabwean", "Southern African", "East African", "Caribbean", "Other"];

export default function BecomeVendor() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    cover_image_url: "https://images.unsplash.com/photo-1572851569977-e18b9ea6edbe?crop=entropy&cs=srgb&fm=jpg&q=85",
    logo_url: "",
    cultural_origin: "Nigerian",
    delivery_config: {
      local_delivery_enabled: true,
      delivery_radius_miles: 5,
      nationwide_shipping_enabled: false,
      local_delivery_fee: 3.99,
      nationwide_shipping_fee: 5.99,
      minimum_order: 15,
      base_postcode: "",
      delivery_zone_prefixes: [],
      estimated_delivery_days: 2,
    },
    contact: { phone: "", whatsapp: "", email: user?.email || "" },
  });

  useEffect(() => {
    if (!user) navigate("/login");
    else if (user.role === "vendor" || user.role === "admin") {
      // Already vendor? Redirect
      api.get("/vendors/mine/current").then(() => navigate("/vendor")).catch(() => {});
    }
  }, [user, navigate]);

  const submit = async () => {
    if (!form.name || !form.description || !form.delivery_config.base_postcode) {
      toast.error("Please fill name, description, and base postcode");
      return;
    }
    setSaving(true);
    try {
      await api.post("/vendors", form);
      await refresh();
      toast.success("Welcome aboard! We'll review your application shortly.");
      navigate("/vendor");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create vendor");
    }
    setSaving(false);
  };

  return (
    <div className="pt-24 pb-24 max-w-3xl mx-auto px-4 md:px-8" data-testid="become-vendor-page">
      <div className="mb-8 text-center">
        <div className="w-16 h-16 rounded-full bg-[#A94438]/10 flex items-center justify-center mx-auto mb-4">
          <Storefront size={32} weight="duotone" className="text-[#A94438]" />
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-3">Become an AfriPantry vendor</h1>
        <p className="text-sm md:text-base text-[#5C5C54] max-w-lg mx-auto mb-6">
          Reach UK-wide customers looking for authentic African groceries. After submission, our team reviews your store and activates it within 48 hours.
        </p>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-2 md:gap-3" data-testid="onboarding-stepper">
        {[
          { n: 1, label: "Store info" },
          { n: 2, label: "Delivery" },
          { n: 3, label: "Contact" },
          { n: 4, label: "Review & submit" },
        ].map((s) => (
          <div key={s.n} className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#A94438] text-white flex items-center justify-center text-xs font-bold">
              {s.n}
            </div>
            <p className="text-[10px] md:text-xs font-bold text-center text-[#1A1A18] leading-tight">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="card-surface p-6 md:p-8 space-y-5">
        <div className="pb-2 border-b border-[#E5E2D9]">
          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#A94438] mb-1">Step 1 of 4</p>
          <h2 className="font-heading font-bold text-lg">Store information</h2>
        </div>
        <div>
          <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Store name *</label>
          <input data-testid="vendor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" placeholder="e.g. Ebony Spice Kitchen" />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Description *</label>
          <textarea data-testid="vendor-desc-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field resize-none" placeholder="Tell customers what makes your pantry special..." />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Cultural origin</label>
          <select data-testid="vendor-origin-select" value={form.cultural_origin} onChange={(e) => setForm({ ...form, cultural_origin: e.target.value })} className="input-field">
            {ORIGINS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Cover image URL</label>
          <input data-testid="vendor-cover-input" value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} className="input-field" />
        </div>

        <div className="border-t border-[#E5E2D9] pt-5">
          <div className="pb-2 mb-4">
            <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#A94438] mb-1">Step 2 of 4</p>
            <h3 className="font-heading font-bold text-lg">Delivery configuration</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Base postcode *</label>
              <input data-testid="vendor-base-postcode" value={form.delivery_config.base_postcode} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, base_postcode: e.target.value.toUpperCase() } })} className="input-field uppercase" placeholder="e.g. E1" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Minimum order (£)</label>
              <input type="number" min="0" step="0.01" data-testid="vendor-min-order" value={form.delivery_config.minimum_order} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, minimum_order: parseFloat(e.target.value) || 0 } })} className="input-field" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Local delivery fee (£)</label>
              <input type="number" min="0" step="0.01" data-testid="vendor-local-fee" value={form.delivery_config.local_delivery_fee} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, local_delivery_fee: parseFloat(e.target.value) || 0 } })} className="input-field" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Nationwide shipping fee (£)</label>
              <input type="number" min="0" step="0.01" data-testid="vendor-nationwide-fee" value={form.delivery_config.nationwide_shipping_fee} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, nationwide_shipping_fee: parseFloat(e.target.value) || 0 } })} className="input-field" />
            </div>
            <label className="flex items-center gap-2 text-sm md:col-span-2">
              <input type="checkbox" data-testid="vendor-nationwide-toggle" checked={form.delivery_config.nationwide_shipping_enabled} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, nationwide_shipping_enabled: e.target.checked } })} />
              Offer UK-wide nationwide shipping
            </label>
          </div>
        </div>

        <div className="border-t border-[#E5E2D9] pt-5">
          <div className="pb-2 mb-4">
            <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#A94438] mb-1">Step 3 of 4</p>
            <h3 className="font-heading font-bold text-lg">Contact</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input data-testid="vendor-phone" placeholder="Phone" value={form.contact.phone} onChange={(e) => setForm({ ...form, contact: { ...form.contact, phone: e.target.value } })} className="input-field" />
            <input data-testid="vendor-whatsapp" placeholder="WhatsApp (with country code)" value={form.contact.whatsapp} onChange={(e) => setForm({ ...form, contact: { ...form.contact, whatsapp: e.target.value } })} className="input-field" />
          </div>
        </div>

        <div className="border-t border-[#E5E2D9] pt-5">
          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#A94438] mb-1">Step 4 of 4</p>
          <h3 className="font-heading font-bold text-lg mb-2">Review & submit</h3>
          <p className="text-xs text-[#5C5C54] mb-4">You can add products after your store is approved.</p>
          <button data-testid="vendor-submit-btn" onClick={submit} disabled={saving} className="btn-primary w-full gap-2 disabled:opacity-50">
            {saving ? "Submitting..." : "Submit for approval"} <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
